import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Collapse, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material';
import Avatar from '@/components/base-component/extended/Avatar';
import MainCard from '@/components/base-component/MainCard';
import useAuth from '@/hooks/useAuth';
import { Logout, Setting2, TextalignJustifycenter, User } from 'iconsax-react';
import EditModal from '@/pages/dashboard/admin-users/component/modal/edit-modal';
import { getPermissionAdminsDisplay, getPermissionUsersDisplay } from '@/utils/DataMapping';
import { useGetMenuMaster } from '@/api/menu';
import Setting from '../modal/Setting';
import { getUrlAvatarPath } from '@/utils/Path';
import avatarHolder from '@/assets/images/avatar-holder.png';
import useAlert from '@/hooks/useAlert';
import useApiGet from '@/hooks/useApiGet';

const UserDrawer = () => {
  const { logout, user } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const theme = useTheme();
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openSetting, setOpenSetting] = useState<boolean>(false);

  const handleOpenEdit = () => setOpenEdit((prevent) => !prevent);
  const handleCloseEdit = () => setOpenEdit(false);

  const handleOpenSetting = () => setOpenSetting((prevent) => !prevent);
  const handleCloseSetting = () => setOpenSetting(false);

  const { data, handleRefresh } = useApiGet(`/account/${user?.activeAccount?.id}`);

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };
  const handleLogoutConfirm = () => {
    showAlert({
      type: 'Confirm',
      title: 'ยืนยันการออกจากระบบ',
      message: 'คุณแน่ใจหรือไม่ว่าต้องการออกจากระบบ?',
      confirmButtonColor: theme.palette.error.main,
      onCancel() {},
      onConfirm() {
        handleLogout();
      }
    });
  };

  useEffect(() => {
    if (!drawerOpen) {
      setOpen(false);
    }
  }, [drawerOpen]);

  return (
    <Box>
      <MainCard border={false} content={false} sx={{ mx: 2, my: 1 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ padding: !drawerOpen ? 1.5 : 1.5, backgroundColor: '#E8EBEE' }}
        >
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                alt="profile user"
                src={data?.avatar ? getUrlAvatarPath(data?.avatar ?? '') : avatarHolder}
                size={!drawerOpen ? 'md' : 'lg'}
              />
              <Stack sx={{ display: !drawerOpen ? 'none' : 'flex' }}>
                <Typography variant="subtitle1">{data?.fullName}</Typography>
                <Box width={'100%'}>
                  <Typography variant="body1" color="neutral" whiteSpace={'normal'}>
                    {data?.userRole === 'USER' ? getPermissionUsersDisplay(data?.permission) : getPermissionAdminsDisplay(data?.permission)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
          <Grid sx={{ display: !drawerOpen ? 'none' : 'flex' }}>
            <IconButton size="large" sx={{ p: 1 }} onClick={() => setOpen((prevent) => !prevent)}>
              <TextalignJustifycenter />
            </IconButton>
          </Grid>
        </Grid>
        <Collapse orientation="vertical" in={open}>
          <Grid container direction={'column'} justifyContent="space-between" sx={{ px: 4, pb: 2, backgroundColor: '#E8EBEE' }} spacing={2}>
            <Grid
              item
              sx={{
                cursor: 'pointer',
                ':hover': {
                  color: `${theme.palette.primary.dark}`
                }
              }}
            >
              <Box onClick={handleOpenEdit}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <User size={18} />
                  <Stack direction="row" spacing={1.25} alignItems="end">
                    <Typography>บัญชีของฉัน</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid
              item
              sx={{
                cursor: 'pointer',
                ':hover': {
                  color: `${theme.palette.primary.dark}`
                }
              }}
            >
              <Box onClick={handleOpenSetting}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Setting2 size={18} />
                  <Stack direction="row" spacing={1.25} alignItems="end">
                    <Typography>ตั้งค่า</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid
              item
              sx={{
                cursor: 'pointer',
                ':hover': {
                  color: `${theme.palette.error.dark}`
                }
              }}
            >
              <Box onClick={handleLogoutConfirm}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Logout size={18} />
                  <Stack direction="row" spacing={1.25} alignItems="end">
                    <Typography>ออกจากระบบ</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </MainCard>

      <Setting open={openSetting} onClose={handleCloseSetting} />

      <EditModal
        data={data}
        open={openEdit}
        onClose={handleCloseEdit}
        onSuccess={() => {
          handleRefresh();
          handleCloseEdit();
          window.location.reload();
        }}
      />
    </Box>
  );
};

export default UserDrawer;
